import { useTranslations } from 'next-intl';

import {
  type SuggestionOption,
  type SuggestionOptionSelection,
  SuggestionOptionButtons,
} from '@/components/SuggestionOptionButtons';
import { VARIANT_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { formatSuggestedOffersResponse } from '@/modules/offers/buyers/helpers/formatSuggestedOffersResponse';
import { getOfferPriceSuggestions } from '@/modules/offers/buyers/helpers/getOfferPriceSuggestions';
import { useSuggestedOffers } from '@/modules/offers/buyers/hooks/useSuggestedOffers';

const RECOMMENDED_INDEX = 1;

type Props = {
  baseCurrentPrice: number;
  currencySymbol: string;
  selectedSuggestedPrice?: number;
  onSuggestedPriceClick: (selection: SuggestionOptionSelection) => void;
  productId: number;
};

export function SuggestedOfferPriceButtons({
  baseCurrentPrice,
  currencySymbol,
  selectedSuggestedPrice,
  onSuggestedPriceClick,
  productId,
}: Props) {
  const t = useTranslations('offers');
  const { web_6874_index_driven_suggested_offers } = useExperiments([
    'web_6874_index_driven_suggested_offers',
  ]);
  const isIndexDrivenSuggestedOffersExpVariant =
    web_6874_index_driven_suggested_offers === VARIANT_IDENTIFIER;
  const { data, isLoading } = useSuggestedOffers({
    enabled: isIndexDrivenSuggestedOffersExpVariant,
    productId: productId.toString(),
  });
  const offerPriceSuggestions =
    (isIndexDrivenSuggestedOffersExpVariant &&
      formatSuggestedOffersResponse(data)) ||
    getOfferPriceSuggestions(baseCurrentPrice);
  const showPlaceholders = isIndexDrivenSuggestedOffersExpVariant && isLoading;

  const options: SuggestionOption[] = offerPriceSuggestions.map(
    (priceSuggestion) => ({
      key: priceSuggestion.percentageDiscount.toString(),
      topLabel: t('SuggestedOfferPrice.PercentageDiscount', {
        percentage: priceSuggestion.percentageDiscount,
      }),
      displayLabel: currencySymbol + priceSuggestion.price,
      value: priceSuggestion.price,
    })
  );

  return (
    <SuggestionOptionButtons
      options={options}
      selectedIndex={selectedSuggestedPrice}
      onSelect={onSuggestedPriceClick}
      recommendedIndex={RECOMMENDED_INDEX}
      recommendationLabel={t('SuggestedOfferPrice.Recommendation')}
      isLoading={showPlaceholders}
    />
  );
}
